// ---------------------------------------------------------------------------
// The enrolment QR code
// ---------------------------------------------------------------------------
// One job: turn the otpauth:// URI Cognito hands back at TOTP setup into
// something an authenticator app can photograph.
//
// Rendered locally, from a bundled encoder, and that is not an implementation
// detail. The URI contains the shared secret — the whole second factor — so
// handing it to an image service to draw would be posting the credential to a
// third party and keeping a copy in their logs. There is no QR service this
// could safely use, only local encoders.
//
// Encoding is async and can fail (an over-long URI has no valid symbol), so
// this renders nothing rather than a broken image, and the sign-in screen keeps
// showing the setup key underneath either way. An authenticator that cannot
// scan, a camera that will not focus, a reader using a desktop app — the text
// key is the path that always works, so the QR is an accelerator on top of it,
// never the only way through.
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react';
import QRCode from 'qrcode';

export default function TotpQrCode({ uri, size = 176, label = 'Scan this with your authenticator app' }) {
  // The URI is held WITH the image rather than beside it, so a render can tell
  // whether the code in hand belongs to the secret currently on screen. Start
  // over and enrol again and the second secret arrives before its encode
  // finishes; a plain src would show the first one's QR in the meantime, and
  // scanning that enrols an authenticator against a secret the pool has
  // already discarded. The mismatch renders nothing instead.
  const [qr, setQr] = useState(null);   // { uri, src }

  useEffect(() => {
    if (!uri) return undefined;

    // Guards a late set: enrolment gets abandoned mid-encode when someone hits
    // Start over, and React warns on an update after unmount.
    let live = true;

    QRCode.toDataURL(uri, {
      errorCorrectionLevel: 'M',
      margin: 2,
      width: size * 2,          // 2x so it stays sharp on a retina display
      color: { dark: '#000000', light: '#ffffff' },
    })
      .then((src) => { if (live) setQr({ uri, src }); })
      .catch((err) => {
        // Not fatal and not worth alarming anyone: the key below still enrols
        // the account. Logged so a URI this cannot encode is discoverable.
        console.error('[totp] could not render the setup QR code:', err?.message || err);
        if (live) setQr(null);
      });

    return () => { live = false; };
  }, [uri, size]);

  const src = qr && qr.uri === uri ? qr.src : null;
  if (!src) return null;

  return (
    <div className="si-qr">
      {/* White plate behind the code, always. A dark theme inverting the page
          under a QR is how you get a symbol no reader will accept — the quiet
          zone and the contrast polarity are part of the format, not styling. */}
      <img src={src} width={size} height={size} alt={label} className="si-qr__img" />
    </div>
  );
}
